import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddressInput, Flex } from '@wegroup/design-system';

// Components
import AdvisoryFlowMainContainer from '../../../ui/advisory-flow/components/app-shell/AdvisoryFlowMainContainer';

// Types
import { Address } from '../../../../../../../types/Common';
import { isEmpty } from 'lodash';
import QuestionGroupActions from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupActions';
import QuestionGroupAssistantQuestion from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupAssistantQuestion';
import { getFlowsDataTestId } from '../../../../utils/testUtils';
import { Inputs } from '../../../../types/Enums';
import { FieldErrors } from 'react-hook-form';
import { handleResponseLanguage, useLocale } from '../../../../../../../i18n';
import { caribbeanCountries } from '../../../../../../common/src/constants/caribbeanCountries';
import useCurrentGroup from '../../../../hooks/useCurrentGroup';
import useAuth from '../../../../../../app/src/features/authentication/hooks/useAuth';
interface Props {
  name: string;
  value?: Address;
  error?: string;
  errors?: FieldErrors<Address>;
  onChange: (arg0?: Address | null) => void;
  previousSubView: () => void;
  nextQuestionHandler: () => void;
  countryCode?: string;
  prefilled?: boolean;
  supportedCountries?: string[];
  partyId?: string;
  isLookupAllowed?: boolean;
}

const AddressSelection: React.FC<React.PropsWithChildren<Props>> = ({
  name,
  value,
  error,
  errors,
  countryCode,
  onChange,
  previousSubView,
  nextQuestionHandler,
  prefilled,
  supportedCountries,
  partyId,
  isLookupAllowed,
}) => {
  const { userCountry } = useLocale();
  const { distribution } = useAuth();
  const [emptyError, setEmptyError] = useState();
  const { t } = useTranslation();
  const currentGroup = useCurrentGroup();
  const addressQuestion = currentGroup.questions.find(
    (q) => q.field === 'address',
  );

  const onSubmitAddress = async () => {
    // List required fields below
    const countryValidationRules = {
      default: ['housenr', 'street', 'city', 'zipcode', 'country_code'],
      caribbean: ['housenr', 'street', 'city', 'country_code'],
    };
    const country = value?.country_code || userCountry;

    // Determine the validation rules based on the country
    let requiredFields = countryValidationRules.default;
    if (caribbeanCountries.includes(country)) {
      requiredFields = countryValidationRules.caribbean;
    }
    // Check if all required fields are present
    for (const field of requiredFields) {
      if (!value?.[field] && addressQuestion?.rules.mandatory) {
        return setEmptyError(t('Please fill in a valid address'));
      }
    }
    await nextQuestionHandler();
  };

  const defaultCountry = supportedCountries?.length
    ? supportedCountries?.includes(distribution?.address?.country_code || '')
      ? distribution?.address?.country_code
      : supportedCountries?.[0]
    : distribution?.address?.country_code;

  return (
    <AdvisoryFlowMainContainer>
      <QuestionGroupAssistantQuestion
        question={handleResponseLanguage(currentGroup.title)}
        description={handleResponseLanguage(currentGroup.description)}
      />
      <Flex
        maxW="700px"
        mx="auto"
        alignItems="flex-start"
        justifyContent="center"
        flexGrow={1}
        px="4"
        py="8"
        w="100%"
      >
        <AddressInput
          name={name}
          value={value}
          error={emptyError || error}
          errors={errors}
          onChange={onChange}
          country={defaultCountry || undefined}
          isPrefilled={!isEmpty(value) && prefilled}
          data-test-id={getFlowsDataTestId(Inputs.ADDRESS, name)}
          supportedCountries={supportedCountries}
          partyId={partyId}
          isLookupAllowed={isLookupAllowed}
        />
      </Flex>
      <QuestionGroupActions
        onBack={previousSubView}
        onContinue={onSubmitAddress}
      />
    </AdvisoryFlowMainContainer>
  );
};

export default AddressSelection;
